import { action, observable } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import SwipeableViews from 'react-swipeable-views';
import classnames from 'classnames';
import { estimateContainer } from '../../../ioc';

import { currency } from 'src/util/formatters';
import { Estimate } from 'src/api';
import { EstimateCard } from '../../estimate-card/desktop/estimate-card';
import { EstimateStore, PriceOptions, PresentationSettingsState } from '../../../stores/estimate.store';
import { hasMemberSavings, memberSavings } from '../../../util/member-savings';
import { PaginationDots } from 'src/core/components/pagination/pagination-dots';
import { ProposalOptionBar } from '../../proposal-option-bar/proposal-option-bar';

import styles from './estimates-list.less';

interface Props {
    estimates: Estimate[] | undefined;
    selectedEstimate: Estimate | undefined;
    priceOptions: PriceOptions | undefined;
    financingEnabled?: boolean;
    financingPresentationSetting?: PresentationSettingsState | undefined;
}

const estimateStore = estimateContainer.get(EstimateStore);

@observer
export class EstimatesList extends React.Component<Props> {
    @observable page = 0;

    @observable private isIframePopoverOpen = false;
    @action 
    toggleIframePopover = (isOpen: boolean) => {
        this.isIframePopoverOpen = isOpen;
    };

    componentWillReceiveProps(nextProps: Props) {
        if (
            nextProps.selectedEstimate &&
            nextProps.selectedEstimate !== this.props.selectedEstimate &&
            nextProps.estimates
        ) {
            const index = nextProps.estimates.indexOf(nextProps.selectedEstimate);
            const page = Math.floor(index / 3);
            if (index >= 0 && page !== this.page) {
                this.handleChangePage(page);
            }
        }
    }

    componentDidMount(): void {
        window.addEventListener('message', message => {
            if (message.data.type === 'enterPopover') {
                this.toggleIframePopover(true)
            } else if (message.data.type === 'leavePopover') {
                this.toggleIframePopover(false);
            }
        });
    }

    handleChangePage = (index: number) => {
        this.page = index;
    };

    render() {
        const { estimates, selectedEstimate, priceOptions, financingEnabled, financingPresentationSetting } = this.props;

        const calculationCard = estimateStore.calculationCard;

        if (!estimates || !selectedEstimate || priceOptions === PriceOptions.Hide) {
            return null;
        }

        const estimatesHaveProposalOptions = estimates.some(
            (e) => e.proposalTagName && e.proposalTagColor
        );

        const estimatesPaged: (Estimate | undefined)[][] = [];
        for (let i = 0; i < estimates.length; i += 3) {
            const slice = estimates.slice(i, i + 3) as (Estimate | undefined)[];
            while (i > 0 && slice.length < 3) {
                slice.push(undefined);
            }
            estimatesPaged.push(slice);
        }

        return (
            <div className={classnames('estimates-top-fill', this.isIframePopoverOpen ? styles.mainTop : styles.main)}>
                <div className={styles.wrapper}>
                    <React.Fragment>
                        <SwipeableViews
                            index={this.page}
                            onChangeIndex={this.handleChangePage}
                            enableMouseEvents
                            style={{ overflowX: 'inherit' }}
                            slideStyle={{ overflow: 'inherit' }}
                        >
                            {estimatesPaged.map((page, index) => (
                                <div key={index} className={styles.slide}>
                                    {page.map((estimate, index) =>
                                        estimate ? (
                                            <EstimateCard
                                                key={estimate.id}
                                                estimate={estimate}
                                                isSelected={
                                                    selectedEstimate &&
                                                    selectedEstimate.id === estimate.id
                                                }
                                                showProposalOption={
                                                    estimatesHaveProposalOptions
                                                }
                                                financingEnabled={financingEnabled}
                                                financingPresentationSetting={financingPresentationSetting || PresentationSettingsState.LeadWithMonthlyPrice}
                                                popOver={this.isIframePopoverOpen}
                                            />
                                        ) : (
                                            <div key={index} className={styles.placeholder} />
                                        )
                                    )}
                                </div>
                            ))}
                        </SwipeableViews>
                        {estimatesPaged.length > 1 && (
                            <PaginationDots
                                dots={estimatesPaged.length}
                                index={this.page}
                                onChangeIndex={this.handleChangePage}
                                className={styles.pagination}
                            />
                        )}
                    </React.Fragment>  
                    {calculationCard && (
                        <div className={styles.calculationCard}>
                            <div className={styles.planNumber}>
                                Plan {calculationCard.planNumber}
                            </div>
                            <div
                                className={styles.closeCalculation}
                                onClick={estimateStore.closeCalculationCard}
                            >
                                X
                            </div>
                            <div className={styles.asLowAs}>As low as</div>
                            {calculationCard.promotionalTerms > 0 && (
                                <div className={styles.promoPayment}>
                                    {currency(calculationCard.promotionalMonthlyPayment)}/month
                                </div>
                            )}
                            {calculationCard.promotionalTerms > 0 && (
                                <div className={styles.promoTerms}>
                                    {calculationCard.promotionalTerms} month promo
                                </div>
                            )}
                            <div className={styles.normalPayment}>
                                {currency(calculationCard.defaultMonthlyPayment ?? 0)}/month
                            </div>
                            <div className={styles.normalTerms}>
                                Terms: {calculationCard.defaultTerms} months
                            </div>
                            <div className={styles.description}>{calculationCard.description}</div>
                        </div>
                    )}
                </div>
            </div>
        );
    }
}
